import React from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  FlexRow,
  PageTile,
  SvgIcon,
  VerifiedIcon,
} from '@make-software/cspr-design';
import { HeaderText, BodyText, Link } from '@block-explorer/ui';
import {
  Avatar,
  DetailsAccordionNodes,
  DetailsAccordionAffiliatedAccounts,
  DetailsAccordionGeneralInfo,
} from '../../../components';
import { Trans, useTranslation } from 'react-i18next';
import {
  AccountInfoResult,
  getAccountInfoLogo,
} from '@block-explorer/entities';
import { includesAccountInVerifiedList } from '../../../utils/account';
import { isObjEmpty } from '../../../utils/object';

const StyledPageTile = styled(PageTile)(({ theme }) => ({
  padding: '24px 0 16px 0',
}));

const AccountRow = styled(FlexRow)(({ theme }) => ({
  padding: '0px 24px 32px 24px',
}));

interface AccountInfoStandardProps {
  accountInfo: AccountInfoResult | undefined;
  accountInfoLoading: boolean | undefined;
  publicKey: string;
}

export const AccountInfoStandard = ({
  accountInfo,
  accountInfoLoading,
  publicKey,
}: AccountInfoStandardProps) => {
  const { t } = useTranslation();

  const logo = getAccountInfoLogo(accountInfo);
  const accountInfoUrl = accountInfo?.url;

  const ownerEmpty = isObjEmpty(accountInfo?.info?.owner);
  const nodesEmpty = isObjEmpty(accountInfo?.info?.nodes);

  const affiliatedAccounts = accountInfo?.info?.owner?.affiliated_accounts;
  const nodes = accountInfo?.info?.nodes;
  const owner = accountInfo?.info?.owner;

  const isAccountVerified = (publicKey): boolean => {
    return includesAccountInVerifiedList(
      accountInfo?.verified_account_hashes,
      publicKey
    );
  };

  return (
    <StyledPageTile>
      <AccountRow gap={32}>
        <FlexColumn>
          {logo ? (
            <Avatar
              src={logo}
              size="medium"
              loading={accountInfoLoading}
              alt={'Account logo'}
            />
          ) : (
            <Avatar
              hash={publicKey}
              size="medium"
              loading={accountInfoLoading}
            />
          )}
        </FlexColumn>
        <FlexColumn>
          <FlexColumn gap={4} justify={'center'}>
            <FlexRow align={'center'}>
              <BodyText variation={'darkGray'} size={2} uppercase>
                <Trans t={t}>Verified owner</Trans>
              </BodyText>
              {isAccountVerified(publicKey) && (
                <SvgIcon
                  src={VerifiedIcon}
                  role={'img'}
                  alt={'Verified icon'}
                  marginLeft
                />
              )}
            </FlexRow>
            <HeaderText scale={'xs'} size={2}>
              {accountInfo?.info?.owner?.name}
            </HeaderText>
          </FlexColumn>
          {accountInfoUrl && (
            <Link color="primaryBlue" href={accountInfoUrl} rel="noopener">
              <BodyText size={3} scale={'sm'}>
                {accountInfoUrl}
              </BodyText>
            </Link>
          )}
        </FlexColumn>
      </AccountRow>
      {ownerEmpty ? null : (
        <DetailsAccordionGeneralInfo
          accordionTitle={<Trans t={t}>Owner info</Trans>}
          owner={owner}
        />
      )}
      {ownerEmpty ? null : (
        <DetailsAccordionAffiliatedAccounts
          accordionTitle={<Trans t={t}>Affiliated accounts</Trans>}
          affiliated_accounts={affiliatedAccounts}
          isAccountVerified={isAccountVerified}
        />
      )}
      {nodesEmpty ? null : (
        <DetailsAccordionNodes
          accordionTitle={<Trans t={t}>Nodes</Trans>}
          otherNodes={nodes}
          isAccountVerified={isAccountVerified}
        />
      )}
    </StyledPageTile>
  );
};
